import React, { Fragment, FunctionComponent } from 'react';
import { List, Collapse, ListItem, ListItemText, FormControlLabel, Radio, Checkbox, FormControl, InputLabel, Select, MenuItem, Grid, FormHelperText } from '@material-ui/core';
import styles from './ReferralService.Styles';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import ServiceDataModel from '../../../../data-models/Service.DataModel';
import { ServicesEnum } from '../../../../support/enums/Services.Enum';
import { toast } from 'react-toastify';
import FacilityTypeEntity from '../../../../entities/FacilityType.Entity';

interface IReferralServiceListProps {
    service: ServiceDataModel;
    handleServiceTypesSelected: (serviceTypeSelected: number[]) => void;
    expanded?: boolean;
    serviceSelected: number;
    setServiceSelected: (serviceSelected: number) => void;
    serviceTypesSelected: Array<number>;
    selectedFacilityTypeIds: number[];
    handleFacilityTypeChange: (facilityTypeIds: number[]) => void;
    facilityTypes: FacilityTypeEntity[];
    showFacilityTypeError?: boolean;
}

export const ReferralServiceList: FunctionComponent<IReferralServiceListProps> = ({
    serviceSelected,
    setServiceSelected,
    service,
    handleServiceTypesSelected,
    expanded,
    serviceTypesSelected,
    selectedFacilityTypeIds,
    handleFacilityTypeChange,
    facilityTypes,
    showFacilityTypeError = false,
}: IReferralServiceListProps) => {
    const classes = styles();
    const [openServiceE, setOpenService] = React.useState(expanded ? expanded : false);

    const MEDICAL_CANVASS_SERVICE_TYPE_ID = 69;
    
    const handleClick = () => {
        setOpenService(!openServiceE);
    };

    const getValidServiceTypeIdsByService = (serviceTypeIds: number[], selectedService: ServiceDataModel): number[] => {
        if (serviceTypeIds.length === 0) return [];
    
        if (!selectedService) return [];
    
        const allServiceTypesByService = selectedService.serviceTypes;
        const serviceTypeIdsSet = new Set(allServiceTypesByService.map(st => st.serviceTypeId));
    
        return serviceTypeIds.filter(id => serviceTypeIdsSet.has(id));
    };

    const myHandleServiceTypeSelected = (serviceTypeSelected: number, serviceId: number) => {
        const newArrayOfServiceTypesNotDiagnostic = [];        
        if (!isNaN(serviceTypeSelected)) {
            setServiceSelected(serviceId);
            newArrayOfServiceTypesNotDiagnostic.push(serviceTypeSelected);
            handleServiceTypesSelected(newArrayOfServiceTypesNotDiagnostic);
        }
    };

    const handleMultipleServiceCheckbox = (serviceTypeSelected: number, serviceId: number) => {                
        let newArrayOfServiceTypes: Array<number> = [...serviceTypesSelected];
        const indexOfSelected = newArrayOfServiceTypes.indexOf(serviceTypeSelected);

        if (serviceId !== serviceSelected) {
            newArrayOfServiceTypes = [];
            setServiceSelected(serviceId);
        } else {
            // Clean service types that do not belong to the selected service
            newArrayOfServiceTypes = getValidServiceTypeIdsByService(newArrayOfServiceTypes, service);
        }

        if (indexOfSelected !== -1 && newArrayOfServiceTypes.length > 0) {
            newArrayOfServiceTypes.splice(indexOfSelected, 1);
            // If Medical Canvass is deselected, clear facilityType
            if (serviceTypeSelected === MEDICAL_CANVASS_SERVICE_TYPE_ID) {
                handleFacilityTypeChange([]);
            }
            handleServiceTypesSelected(newArrayOfServiceTypes);
        } else if (indexOfSelected === -1 && !isNaN(serviceTypeSelected)) {
            if (serviceId === 6 && newArrayOfServiceTypes.length === 2) {
                toast.warning('⚠️ ' + 'you only can select up to 2 service types');
            } else if (newArrayOfServiceTypes.length <= 2 || serviceId == ServicesEnum.SIU) {
                newArrayOfServiceTypes.push(serviceTypeSelected);
                handleServiceTypesSelected(newArrayOfServiceTypes);
            } else {
                toast.warning('⚠️ ' + 'you only can select up to 3 services');
            }
        }
    };

    const getTitle = () => (
        <>
            <span className={classes.listItemTitle}>{service.service.serviceName}</span>
            <span className={classes.subtitleDiagnostic}>
                {openServiceE && service.service.serviceId === ServicesEnum.Diagnostic && 'Select up to 3'}
            </span>
        </>
    );

    return (
        <>
            {service.serviceTypes.length > 0 ? (
                <>
                    <ListItem className={classes.serviceTitle} button onClick={handleClick}>
                        {openServiceE ? <ExpandLess /> : <ExpandMore />}
                        <ListItemText primary={getTitle()} />
                    </ListItem>
                    <Collapse in={openServiceE} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {service.service.serviceId === ServicesEnum.Diagnostic ||
                            service.service.serviceId === ServicesEnum.TT || service.service.serviceId === ServicesEnum.SIU
                                ? service.serviceTypes.map((servicetype, sindex) => {
                                      const isMedicalCanvass = servicetype.serviceTypeId === MEDICAL_CANVASS_SERVICE_TYPE_ID;
                                      const showDropdown = isMedicalCanvass && serviceTypesSelected.includes(MEDICAL_CANVASS_SERVICE_TYPE_ID);
                                      
                                      return (
                                          <ListItem key={servicetype.serviceTypeId} className={classes.nested}>
                                              <Grid container spacing={1} alignItems="center">
                                                  <Grid item xs={showDropdown ? 5 : 12}>
                                                      <ListItemText>
                                                          <FormControlLabel
                                                              control={
                                                                  <Checkbox
                                                                      className={classes.radioButton}
                                                                      checked={
                                                                          serviceTypesSelected.findIndex(
                                                                              (sts) => sts === servicetype.serviceTypeId,
                                                                          ) !== -1
                                                                      }
                                                                      onChange={(event) => {
                                                                          handleMultipleServiceCheckbox(
                                                                              +(event.target as HTMLInputElement).value,
                                                                              servicetype.serviceId,
                                                                          );
                                                                      }}
                                                                      value={servicetype.serviceTypeId}
                                                                  />
                                                              }
                                                              label={
                                                                  <span className={classes.radioButtonLabel}>
                                                                      {servicetype.serviceTypeName}
                                                                  </span>
                                                              }
                                                          />
                                                      </ListItemText>
                                                  </Grid>
                                                  {showDropdown && (
                                                      <Grid item xs={7}>
                                                          <FormControl 
                                                              fullWidth 
                                                              required
                                                              error={showFacilityTypeError && (!selectedFacilityTypeIds || selectedFacilityTypeIds.length === 0)}
                                                              size="small"
                                                          >
                                                              <InputLabel id="facility-type-label">
                                                                  Facility Options
                                                              </InputLabel>
                                                              <Select
                                                                  labelId="facility-type-label"
                                                                  id="facility-type-select"
                                                                  multiple
                                                                  value={selectedFacilityTypeIds}
                                                                  onChange={(event) => {
                                                                      const value = event.target.value as number[];
                                                                      handleFacilityTypeChange(value);
                                                                  }}
                                                                  renderValue={(selected) => {
                                                                      const selectedArray = selected as number[];
                                                                      if (selectedArray.length === 0) {
                                                                          return <em>Select facilities</em>;
                                                                      }
                                                                      if (selectedArray.length === 1) {
                                                                          const facility = facilityTypes.find(f => f.facilityTypeId === selectedArray[0]);
                                                                          return facility?.facilityTypeName || '';
                                                                      }
                                                                      return `${selectedArray.length} facilities selected`;
                                                                  }}
                                                              >
                                                                  {facilityTypes.map((facilityType) => (
                                                                      <MenuItem 
                                                                          key={facilityType.facilityTypeId} 
                                                                          value={facilityType.facilityTypeId}
                                                                          dense
                                                                          className={classes.facilityTypeMenuItem}
                                                                      >
                                                                          <Checkbox 
                                                                              size="small"
                                                                              checked={selectedFacilityTypeIds.indexOf(facilityType.facilityTypeId) > -1}
                                                                          />
                                                                          <ListItemText 
                                                                              primary={facilityType.facilityTypeName} 
                                                                              className={classes.facilityTypeItemText}
                                                                          />
                                                                      </MenuItem>
                                                                  ))}
                                                              </Select>
                                                              {showFacilityTypeError && (!selectedFacilityTypeIds || selectedFacilityTypeIds.length === 0) && (
                                                                  <FormHelperText>Required</FormHelperText>
                                                              )}
                                                          </FormControl>
                                                      </Grid>
                                                  )}
                                              </Grid>
                                          </ListItem>
                                      );
                                  })
                                : service.serviceTypes.map((servicetype, sindex) => (
                                      <ListItem key={servicetype.serviceTypeId} className={classes.nested}>
                                          <ListItemText>
                                              <FormControlLabel
                                                  control={
                                                      <Radio
                                                          className={classes.radioButton}
                                                          checked={
                                                              serviceTypesSelected.findIndex(
                                                                  (sts) => sts === servicetype.serviceTypeId,
                                                              ) !== -1
                                                          }
                                                          onChange={(event) =>
                                                              myHandleServiceTypeSelected(
                                                                  +(event.target as HTMLInputElement).value,
                                                                  service.service.serviceId,
                                                              )
                                                          }
                                                          value={servicetype.serviceTypeId}
                                                      />
                                                  }
                                                  label={
                                                      <span className={classes.radioButtonLabel}>
                                                          {servicetype.serviceTypeName}
                                                      </span>
                                                  }
                                              />
                                          </ListItemText>
                                      </ListItem>
                                  ))}
                        </List>
                    </Collapse>
                </>
            ) : (
                ''
            )}
        </>
    );
};